import { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

/**
 * Drag-to-resize on a right-anchored drawer's left edge, shared by every panel that has one so
 * they behave and look identical. The width is kept in a ref while dragging so the pointer stays
 * glued to the handle, and only committed to storage on release.
 *
 * Remembering the width is a per-viewer convenience: a private window or a blocked store means the
 * drawer opens at its default, never an error.
 */
export function useDrawerResize(options: { storageKey: string; defaultWidth: number; minWidth: number }) {
  const { storageKey, defaultWidth, minWidth } = options;
  const [width, setWidth] = useState(() => readStoredWidth(storageKey, defaultWidth, minWidth));
  const [resizing, setResizing] = useState(false);
  const widthRef = useRef(width);

  const startResize = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    setResizing(true);
  }, []);

  useEffect(() => {
    if (!resizing) return;

    const onMove = (event: MouseEvent) => {
      const next = Math.min(Math.max(window.innerWidth - event.clientX, minWidth), window.innerWidth);
      widthRef.current = next;
      setWidth(next);
    };
    const onUp = () => {
      setResizing(false);
      try {
        localStorage.setItem(storageKey, String(Math.round(widthRef.current)));
      } catch {
        // Not being able to remember the width is not worth failing the interaction over.
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing, storageKey, minWidth]);

  return { width, resizing, startResize };
}

function readStoredWidth(storageKey: string, fallback: number, minWidth: number): number {
  try {
    const stored = Number(localStorage.getItem(storageKey));
    return Number.isFinite(stored) && stored >= minWidth ? stored : fallback;
  } catch {
    return fallback;
  }
}

/** The grab strip itself. Hidden on xs, where a drawer is full width and there is nothing to drag. */
export function DrawerResizeHandle({
  onMouseDown,
  resizing,
  label,
}: {
  onMouseDown: (event: React.MouseEvent) => void;
  resizing: boolean;
  label: string;
}) {
  return (
    <Box
      onMouseDown={onMouseDown}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 6,
        cursor: 'col-resize',
        zIndex: 3,
        display: { xs: 'none', sm: 'block' },
        bgcolor: resizing ? 'primary.main' : 'transparent',
        '&:hover': { bgcolor: 'primary.light' },
      }}
    />
  );
}
